<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  initialAnswers: { type: Object, default: () => ({}) },
  disabled: { type: Boolean, default: false }
})

const emit = defineEmits(['complete'])

const steps = [
  {
    id: 'goals',
    title: 'Investment Goals',
    description: 'Define your primary investment objectives',
    questions: ['goal1Type', 'goal1Horizon']
  },
  {
    id: 'risk',
    title: 'Risk & Style',
    description: 'Your risk comfort and investment preferences',
    questions: ['riskTolerance', 'allocationStyle']
  },
  {
    id: 'preferences',
    title: 'Portfolio Preferences',
    description: 'Additional portfolio customization options',
    questions: ['internationalExposure', 'excludeSmallCap', 'thematicInvesting']
  }
]

const allQuestions = {
  goal1Type: {
    id: 'goal1Type',
    label: 'What is your primary investment goal?',
    type: 'select',
    options: [
      { value: 'Wealth', label: 'Wealth growth' },
      { value: 'Retirement', label: 'Retirement planning' },
      { value: 'ShortTerm', label: 'Short-term goals (1-3 years)' },
      { value: 'Emergency', label: 'Emergency fund' }
    ],
    hint: 'This drives the core structure of your portfolio',
    apiKey: 'goal1Type'
  },
  goal1Horizon: {
    id: 'goal1Horizon',
    label: 'Investment time horizon',
    type: 'select',
    options: [
      { value: '1-3y', label: '1-3 years' },
      { value: '3-5y', label: '3-5 years' },
      { value: '5+y', label: '5+ years (recommended)' }
    ],
    hint: 'Longer horizons enable better diversified equity exposure',
    apiKey: 'goal1Horizon'
  },
  riskTolerance: {
    id: 'riskTolerance',
    label: 'Risk tolerance',
    type: 'select',
    options: [
      { value: 'Low', label: 'Conservative - Preserve capital' },
      { value: 'Moderate', label: 'Moderate - Balanced growth' },
      { value: 'High', label: 'Aggressive - Maximum growth' }
    ],
    hint: 'Determines equity vs debt allocation',
    apiKey: 'riskTolerance'
  },
  allocationStyle: {
    id: 'allocationStyle',
    label: 'Allocation style preference',
    type: 'select',
    options: [
      { value: 'Dynamic', label: 'Dynamic - Active tactical shifts' },
      { value: 'Structured', label: 'Structured - Rule-based allocation' }
    ],
    hint: 'Dynamic allows managers to adjust; Structured maintains fixed ratios',
    apiKey: 'allocationStyle'
  },
  internationalExposure: {
    id: 'internationalExposure',
    label: 'Add global market exposure?',
    type: 'toggle',
    hint: 'Includes international equity funds for diversification',
    apiKey: 'internationalExposure'
  },
  excludeSmallCap: {
    id: 'excludeSmallCap',
    label: 'Exclude small-cap funds?',
    type: 'toggle',
    hint: 'Small-caps offer growth but with higher volatility',
    apiKey: 'excludeSmallCap'
  },
  thematicInvesting: {
    id: 'thematicInvesting',
    label: 'Include thematic/focused funds?',
    type: 'toggle',
    hint: 'Adds concentrated sector or thematic exposure',
    apiKey: 'thematicInvesting'
  }
}

const localAnswers = ref({ ...props.initialAnswers })
const currentStepIndex = ref(0)
const currentStep = computed(() => steps[currentStepIndex.value])

const isStepValid = computed(() => {
  const ids = currentStep.value.questions
  return ids.every(qid => {
    const q = allQuestions[qid]
    if (!q || q.type === 'toggle') return true
    const key = q.apiKey || q.id
    return !!localAnswers.value[key]
  })
})

const isAllValid = computed(() => {
  return Object.values(allQuestions).every(q => {
    if (q.type === 'toggle') return true
    const key = q.apiKey || q.id
    return !!localAnswers.value[key]
  })
})

const progressPercent = computed(() => {
  const required = Object.values(allQuestions).filter(q => q.type !== 'toggle')
  const answered = required.filter(q => {
    const key = q.apiKey || q.id
    return !!localAnswers.value[key]
  }).length
  return Math.round((answered / required.length) * 100)
})

const goNext = () => {
  if (!isStepValid.value || currentStepIndex.value >= steps.length - 1) return
  currentStepIndex.value++
}

const goPrev = () => {
  if (currentStepIndex.value === 0) return
  currentStepIndex.value--
}

const setAnswer = (q, value) => {
  const key = q.apiKey || q.id
  localAnswers.value[key] = value
}

const handleSubmit = () => {
  if (!isAllValid.value) return
  emit('complete', localAnswers.value)
}
</script>

<template>
  <div class="sip-questionnaire">
    <div class="progress-section">
      <div class="progress-bar-container">
        <div class="progress-bar-fill" :style="{ width: progressPercent + '%' }" />
      </div>
      <span class="progress-text">{{ progressPercent }}% complete</span>
    </div>

    <div class="step-header">
      <h3>{{ currentStep.title }}</h3>
      <p class="step-description">{{ currentStep.description }}</p>
    </div>

    <div class="questions-container">
      <div
        v-for="qid in currentStep.questions"
        :key="qid"
        class="question-block"
      >
        <label class="question-label">{{ allQuestions[qid].label }}</label>
        <p class="question-hint">{{ allQuestions[qid].hint }}</p>

        <select
          v-if="allQuestions[qid].type === 'select'"
          class="question-select"
          :disabled="disabled"
          :value="localAnswers[allQuestions[qid].apiKey] || ''"
          @change="setAnswer(allQuestions[qid], $event.target.value)"
        >
          <option disabled value="">Select an option</option>
          <option
            v-for="opt in allQuestions[qid].options"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>

        <label v-else-if="allQuestions[qid].type === 'toggle'" class="toggle-container">
          <input
            type="checkbox"
            :disabled="disabled"
            :checked="!!localAnswers[allQuestions[qid].apiKey]"
            @change="setAnswer(allQuestions[qid], $event.target.checked)"
            class="toggle-checkbox"
          />
          <span class="toggle-label">
            {{ localAnswers[allQuestions[qid].apiKey] ? 'Yes' : 'No (default)' }}
          </span>
        </label>
      </div>
    </div>

    <div class="navigation-buttons">
      <button
        class="btn-secondary"
        @click="goPrev"
        :disabled="currentStepIndex === 0 || disabled"
      >
        ← Previous
      </button>
      <button
        v-if="currentStepIndex < steps.length - 1"
        class="btn-primary"
        @click="goNext"
        :disabled="!isStepValid || disabled"
      >
        Next →
      </button>
      <button
        v-else
        class="btn-primary"
        @click="handleSubmit"
        :disabled="!isAllValid || disabled"
      >
        Review Answers
      </button>
    </div>
  </div>
</template>

<style scoped>
.sip-questionnaire {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.progress-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-bar-container {
  flex: 1;
  height: 6px;
  background: #e9ecef;
  border-radius: 999px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--an-primary), #22c55e);
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.85rem;
  color: #868e96;
  font-weight: 600;
}

.step-header {
  text-align: center;
}

.step-header h3 {
  margin: 0 0 8px 0;
  color: var(--an-primary);
  font-size: 1.3rem;
}

.step-description {
  margin: 0;
  color: var(--an-text-secondary);
  font-size: 0.9rem;
}

.questions-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.question-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.question-label {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--an-text-primary);
}

.question-hint {
  margin: 0;
  font-size: 0.85rem;
  color: var(--an-text-secondary);
  font-style: italic;
}

.question-select {
  padding: 10px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  transition: border-color 0.2s;
}

.question-select:focus {
  outline: none;
  border-color: var(--an-primary);
}

.toggle-container {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.toggle-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.toggle-label {
  font-size: 0.9rem;
  font-weight: 500;
}

.navigation-buttons {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-top: 8px;
}

.btn-primary,
.btn-secondary {
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: var(--an-primary);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0a6454;
}

.btn-secondary {
  background: #f1f3f5;
  color: #495057;
}

.btn-secondary:hover:not(:disabled) {
  background: #e9ecef;
}

.btn-primary:disabled,
.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
